import { formatDate } from "@/lib/format";
import { normalizeText, splitQuery } from "@/lib/normalize";
import { getPriceBandFullLabel, getPriceBandLabel } from "@/lib/pricing";
import {
  getAttributeDefinition,
  getGenreById,
  getGenreName,
  getGenreNames,
  isGenreVisibleForAudience,
  resolveGenreIds,
} from "@/lib/taxonomy";
import {
  EMPTY_FILTERS,
  type AttributeFilter,
  type AudienceType,
  type ContentTypeId,
  type PriceBand,
  type SortKey,
  type Work,
  type WorkFilters,
} from "@/types/work";
import type { AudienceId } from "@/types/taxonomy";

/**
 * 検索条件のURL表現・チップ表示・モード整合の補正。
 * 検索の実行（スコアリング・並び替え）は search.ts が担当する。
 */

const typeNames: Record<string, string> = {
  video: "動画",
  manga: "漫画",
  ebook: "電子書籍",
  book: "書籍",
  doujin: "同人",
  voice: "音声",
  cg: "CG",
  game: "ゲーム",
  goods: "グッズ",
  other: "その他",
};

const audienceNames: Record<string, string> = {
  male: "男性向け",
  female: "女性向け",
  common: "共通",
  unclassified: "未分類",
};

/** 属性レンジのシリアライズ区切り（IDに`-`が入るため、範囲は`~`で区切る） */
const ATTR_RANGE_SEPARATOR = "~";

function serializeAttribute(filter: AttributeFilter): string {
  const min = filter.min === null ? "" : String(filter.min);
  const max = filter.max === null ? "" : String(filter.max);
  return `${filter.attributeId}:${min}${ATTR_RANGE_SEPARATOR}${max}`;
}

function parseAttributeTokens(values: string[]): AttributeFilter[] {
  const result: AttributeFilter[] = [];
  values.forEach((raw) => {
    const [id, range] = raw.split(":");
    if (!id || !range) return;
    const [minRaw, maxRaw] = range.split(ATTR_RANGE_SEPARATOR);
    const minValue = minRaw !== undefined && minRaw !== "" ? Number(minRaw) : null;
    const maxValue = maxRaw !== undefined && maxRaw !== "" ? Number(maxRaw) : null;
    const min = minValue !== null && Number.isFinite(minValue) ? minValue : null;
    const max = maxValue !== null && Number.isFinite(maxValue) ? maxValue : null;
    if (min === null && max === null) return;
    if (result.some((item) => item.attributeId === id.trim())) return;
    result.push({ attributeId: id.trim(), min, max });
  });
  return result;
}

function describeAttribute(filter: AttributeFilter): string {
  const definition = getAttributeDefinition(filter.attributeId);
  const name = definition?.name ?? filter.attributeId;
  const unit = definition?.unit ?? "";
  if (filter.min !== null && filter.max !== null) return `${name}：${filter.min}〜${filter.max}${unit}`;
  if (filter.min !== null) return `${name}：${filter.min}${unit}以上`;
  if (filter.max !== null) return `${name}：${filter.max}${unit}以下`;
  return `${name}：指定なし`;
}

export function modeLabel(mode: AudienceType): string {
  return mode === "female" ? "女性向け" : "男性向け";
}

/** モードで選べる「対象ユーザー」の値（反対モードの指定は許可しない） */
export function allowedAudienceForMode(mode: AudienceType): Array<WorkFilters["audience"]> {
  return ["all", mode, "common"];
}

export interface FilterAdjustment {
  kind: "audience" | "genre";
  message: string;
}

export interface SanitizedFilters {
  filters: WorkFilters;
  adjustments: FilterAdjustment[];
}

/**
 * モードと矛盾する検索条件を取り除き、利用者に説明できる形で返す。
 * 例：女性向けモードなのに「男性向け」が指定されている / 男性専用ジャンルが残っている。
 */
export function sanitizeFiltersForMode(filters: WorkFilters, mode: AudienceType): SanitizedFilters {
  const adjustments: FilterAdjustment[] = [];
  const next: WorkFilters = { ...filters };

  if (!allowedAudienceForMode(mode).includes(filters.audience)) {
    adjustments.push({
      kind: "audience",
      message: `${modeLabel(mode)}モードのため、「${audienceNames[filters.audience] ?? filters.audience}」の対象指定を解除しました。`,
    });
    next.audience = "all";
  }

  const removedGenres = filters.genres.filter((id) => {
    const genre = getGenreById(id);
    if (!genre) return false;
    return !isGenreVisibleForAudience(genre, mode);
  });
  if (removedGenres.length > 0) {
    next.genres = filters.genres.filter((id) => !removedGenres.includes(id));
    adjustments.push({
      kind: "genre",
      message: `${modeLabel(mode)}モードでは選べないジャンル（${removedGenres
        .map((id) => getGenreName(id))
        .join("・")}）を条件から外しました。`,
    });
  }

  return { filters: next, adjustments };
}

export interface HighlightPart {
  text: string;
  match: boolean;
}

/** 検索語のハイライト用に、元の文字列を一致/不一致へ分割する */
export function highlightParts(text: string, query: string): HighlightPart[] {
  const tokens = splitQuery(query);
  if (tokens.length === 0) return [{ text, match: false }];

  let normalized = "";
  const indexMap: number[] = [];
  for (let index = 0; index < text.length; index += 1) {
    const piece = normalizeText(text[index]);
    for (let charIndex = 0; charIndex < piece.length; charIndex += 1) {
      normalized += piece[charIndex];
      indexMap.push(index);
    }
  }

  const marks = new Array<boolean>(text.length).fill(false);
  tokens.forEach((token) => {
    let position = normalized.indexOf(token);
    while (position !== -1) {
      const start = indexMap[position];
      const endIndex = Math.min(position + token.length - 1, indexMap.length - 1);
      const end = indexMap[endIndex] ?? start;
      for (let index = start; index <= end; index += 1) marks[index] = true;
      position = normalized.indexOf(token, position + token.length);
    }
  });

  const parts: HighlightPart[] = [];
  let buffer = "";
  let current = marks[0] ?? false;
  for (let index = 0; index < text.length; index += 1) {
    if (marks[index] === current) {
      buffer += text[index];
    } else {
      parts.push({ text: buffer, match: current });
      buffer = text[index];
      current = marks[index];
    }
  }
  if (buffer) parts.push({ text: buffer, match: current });
  return parts;
}

export function filtersToParams(filters: WorkFilters, sort: SortKey, page = 1): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.q.trim()) params.set("q", filters.q.trim());
  if (filters.audience !== "all") params.set("audience", filters.audience);
  if (filters.types.length > 0) params.set("type", filters.types.join(","));
  if (filters.genres.length > 0) params.set("genre", filters.genres.join(","));
  if (filters.genres.length > 1 && filters.genreMatch === "and") params.set("gmode", "and");
  if (filters.person) params.set("person", filters.person);
  if (filters.makers.length > 0) params.set("maker", filters.makers.join(","));
  if (filters.days !== null) params.set("days", String(filters.days));
  if (filters.releaseStatus !== "any") params.set("release", filters.releaseStatus);
  if (filters.priceBands.length > 0) params.set("price", filters.priceBands.join(","));
  if (filters.attributes.length > 0) {
    params.set("attr", filters.attributes.map(serializeAttribute).join(","));
  }
  if (filters.minRating !== null) params.set("rating", String(filters.minRating));
  if (filters.video) params.set("video", filters.video);
  if (filters.sale) params.set("sale", filters.sale);
  if (filters.series) params.set("series", filters.series);
  if (sort !== "relevance") params.set("sort", sort);
  if (page > 1) params.set("page", String(page));
  return params;
}

function readList(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) return [];
  return raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

const VALID_TYPES: ContentTypeId[] = [
  "video",
  "manga",
  "ebook",
  "book",
  "doujin",
  "voice",
  "cg",
  "game",
  "goods",
  "other",
];

const VALID_PRICE_BANDS: PriceBand[] = ["free", "under500", "500to1000", "1000to3000", "over3000"];

export function parseSort(value: string | null): SortKey {
  const allowed: SortKey[] = ["relevance", "popular", "newest", "oldest", "priceAsc", "priceDesc", "rating"];
  return allowed.includes(value as SortKey) ? (value as SortKey) : "relevance";
}

/** URLのクエリ文字列を検索条件へ変換し、モードと矛盾する条件を補正する */
export function parseFilters(params: URLSearchParams, mode: AudienceType): SanitizedFilters {
  const audienceParam = params.get("audience");
  const audience: WorkFilters["audience"] =
    audienceParam === "male" || audienceParam === "female" || audienceParam === "common" || audienceParam === "unclassified"
      ? (audienceParam as AudienceId)
      : "all";

  const daysRaw = params.get("days");
  const days = daysRaw && !Number.isNaN(Number(daysRaw)) ? Number(daysRaw) : null;
  const ratingRaw = params.get("rating");
  const minRating = ratingRaw && !Number.isNaN(Number(ratingRaw)) ? Number(ratingRaw) : null;
  const video = params.get("video");
  const sale = params.get("sale");
  const series = params.get("series");
  const releaseParam = params.get("release");
  const releaseStatus = releaseParam === "released" || releaseParam === "upcoming" ? releaseParam : "any";

  const raw: WorkFilters = {
    ...EMPTY_FILTERS,
    q: params.get("q") || "",
    audience,
    types: readList(params, "type").filter((type): type is ContentTypeId =>
      VALID_TYPES.includes(type as ContentTypeId),
    ),
    genres: resolveGenreIds(readList(params, "genre")),
    genreMatch: params.get("gmode") === "and" ? "and" : "or",
    person: params.get("person") || "",
    makers: readList(params, "maker"),
    days,
    releaseStatus,
    priceBands: readList(params, "price").filter((band): band is PriceBand =>
      VALID_PRICE_BANDS.includes(band as PriceBand),
    ),
    attributes: parseAttributeTokens(readList(params, "attr")),
    minRating,
    video: video === "yes" || video === "no" ? video : null,
    sale: sale === "yes" || sale === "no" ? sale : null,
    series: series === "single" || series === "series" ? series : null,
  };

  return sanitizeFiltersForMode(raw, mode);
}

/** 条件だけを欲しい場合の薄いラッパー（補正内容は parseFilters で取得できる） */
export function paramsToFilters(params: URLSearchParams, mode: AudienceType): WorkFilters {
  return parseFilters(params, mode).filters;
}

export interface FilterChip {
  id: string;
  kind:
    | "q"
    | "audience"
    | "type"
    | "genre"
    | "genreMatch"
    | "person"
    | "maker"
    | "days"
    | "release"
    | "price"
    | "rating"
    | "video"
    | "sale"
    | "series"
    | "attribute";
  label: string;
  value?: string;
}

export function getFilterChips(filters: WorkFilters): FilterChip[] {
  const chips: FilterChip[] = [];
  if (filters.q.trim()) chips.push({ id: "q", kind: "q", label: `キーワード：${filters.q.trim()}` });
  chips.push({
    id: "audience",
    kind: "audience",
    label: filters.audience === "all" ? "対象：すべて" : `対象：${audienceNames[filters.audience]}`,
  });
  filters.types.forEach((type) =>
    chips.push({ id: `type-${type}`, kind: "type", label: `形式：${typeNames[type]}`, value: type }),
  );
  filters.genres.forEach((genreId) =>
    chips.push({ id: `genre-${genreId}`, kind: "genre", label: getGenreName(genreId), value: genreId }),
  );
  if (filters.genres.length > 1) {
    chips.push({
      id: "genre-match",
      kind: "genreMatch",
      label: filters.genreMatch === "and" ? "ジャンル：すべて含む" : "ジャンル：いずれか",
    });
  }
  if (filters.person) chips.push({ id: "person", kind: "person", label: `人物：${filters.person}` });
  filters.makers.forEach((maker) =>
    chips.push({ id: `maker-${maker}`, kind: "maker", label: `メーカー：${maker}`, value: maker }),
  );
  if (filters.days !== null) {
    const label = filters.days === 365 ? "直近1年" : `直近${filters.days}日`;
    chips.push({ id: "days", kind: "days", label: `発売日：${label}`, value: String(filters.days) });
  }
  if (filters.releaseStatus !== "any") {
    chips.push({
      id: "release",
      kind: "release",
      label: `発売状況：${filters.releaseStatus === "upcoming" ? "発売予定" : "発売済み"}`,
      value: filters.releaseStatus,
    });
  }
  filters.priceBands.forEach((band) =>
    chips.push({ id: `price-${band}`, kind: "price", label: `価格：${getPriceBandLabel(band)}`, value: band }),
  );
  filters.attributes.forEach((filter) =>
    chips.push({
      id: `attr-${filter.attributeId}`,
      kind: "attribute",
      label: describeAttribute(filter),
      value: filter.attributeId,
    }),
  );
  if (filters.minRating !== null)
    chips.push({ id: "rating", kind: "rating", label: `評価：${filters.minRating}以上`, value: String(filters.minRating) });
  if (filters.video)
    chips.push({ id: "video", kind: "video", label: `動画：${filters.video === "yes" ? "あり" : "なし"}`, value: filters.video });
  if (filters.sale)
    chips.push({ id: "sale", kind: "sale", label: `セール：${filters.sale === "yes" ? "対象" : "対象外"}`, value: filters.sale });
  if (filters.series)
    chips.push({
      id: "series",
      kind: "series",
      label: `シリーズ：${filters.series === "series" ? "シリーズ作品" : "単体作品"}`,
      value: filters.series,
    });
  return chips;
}

export function removeChip(filters: WorkFilters, chip: FilterChip): WorkFilters {
  const next: WorkFilters = { ...filters };
  switch (chip.kind) {
    case "q":
      next.q = "";
      break;
    case "audience":
      next.audience = "all";
      break;
    case "type":
      next.types = next.types.filter((type) => type !== (chip.value as ContentTypeId));
      break;
    case "genre":
      next.genres = next.genres.filter((genre) => genre !== chip.value);
      break;
    case "genreMatch":
      next.genreMatch = "or";
      break;
    case "person":
      next.person = "";
      break;
    case "maker":
      next.makers = next.makers.filter((maker) => maker !== chip.value);
      break;
    case "days":
      next.days = null;
      break;
    case "release":
      next.releaseStatus = "any";
      break;
    case "price":
      next.priceBands = next.priceBands.filter((band) => band !== chip.value);
      break;
    case "rating":
      next.minRating = null;
      break;
    case "video":
      next.video = null;
      break;
    case "sale":
      next.sale = null;
      break;
    case "series":
      next.series = null;
      break;
    case "attribute":
      next.attributes = next.attributes.filter((filter) => filter.attributeId !== chip.value);
      break;
    default:
      break;
  }
  return next;
}

export function countAdvancedFilters(filters: WorkFilters): number {
  return (
    filters.types.length +
    filters.genres.length +
    (filters.person ? 1 : 0) +
    filters.makers.length +
    (filters.days !== null ? 1 : 0) +
    (filters.releaseStatus !== "any" ? 1 : 0) +
    filters.priceBands.length +
    (filters.minRating !== null ? 1 : 0) +
    (filters.video ? 1 : 0) +
    (filters.sale ? 1 : 0) +
    (filters.series ? 1 : 0) +
    filters.attributes.length
  );
}

export function getRelaxSuggestions(filters: WorkFilters): string[] {
  const suggestions: string[] = [];
  if (filters.genres.length > 1 && filters.genreMatch === "and") {
    suggestions.push("ジャンルの条件を「いずれか（OR）」に変更する");
  }
  if (filters.days !== null) suggestions.push("発売日の指定を「すべて」に広げる");
  if (filters.releaseStatus === "upcoming") suggestions.push("発売状況を「指定なし」または「発売済み」に変更する");
  if (filters.minRating !== null) suggestions.push("レビュー評価の条件を外す（未評価の作品も含める）");
  if (filters.priceBands.length > 0) suggestions.push("価格帯の条件を減らす");
  if (filters.genres.length > 1) suggestions.push("ジャンルの選択を1つに絞る");
  if (filters.makers.length > 0) suggestions.push("メーカー・出版社の指定を外す");
  if (filters.person) suggestions.push(`「${filters.person}」の指定を外す`);
  if (filters.video) suggestions.push("サンプル動画の条件を「指定なし」にする");
  if (filters.series) suggestions.push("シリーズ条件を「指定なし」にする");
  if (filters.attributes.length > 0) suggestions.push("属性の範囲（収録時間・ページ数など）を広げる");
  if (filters.types.length > 0) suggestions.push("作品形式の指定を外す");
  if (suggestions.length === 0) suggestions.push("キーワードを短くする、または別の表記で試す");
  return suggestions.slice(0, 3);
}

export function describeFilters(filters: WorkFilters): string {
  const parts: string[] = [];
  if (filters.q.trim()) parts.push(`「${filters.q.trim()}」`);
  if (filters.types.length > 0) parts.push(filters.types.map((type) => typeNames[type]).join("・"));
  if (filters.genres.length > 0) {
    const names = getGenreNames(filters.genres).join("・");
    parts.push(filters.genreMatch === "and" && filters.genres.length > 1 ? `${names}（すべて）` : names);
  }
  if (filters.makers.length > 0) parts.push(filters.makers.join("・"));
  if (filters.days !== null) parts.push(`直近${filters.days}日`);
  if (filters.releaseStatus !== "any") {
    parts.push(filters.releaseStatus === "upcoming" ? "発売予定" : "発売済み");
  }
  if (filters.priceBands.length > 0) parts.push(filters.priceBands.map((band) => getPriceBandFullLabel(band)).join("・"));
  if (filters.attributes.length > 0) parts.push(filters.attributes.map(describeAttribute).join(" / "));
  if (filters.minRating !== null) parts.push(`評価${filters.minRating}以上`);
  if (parts.length === 0) return "すべての作品";
  return parts.join(" / ");
}

export function formatSearchDescription(filters: WorkFilters): string {
  const base = filters.audience === "female" ? "女性向け" : "男性向け";
  return `${base}作品の検索結果。${describeFilters(filters)}の条件で絞り込んでいます。`;
}

export function getFilterSummaryDate(days: number | null): string {
  if (days === null) return "すべて";
  if (days === 365) return "直近1年";
  return `直近${days}日`;
}

export function getWorkReleaseLabel(work: Work): string {
  return work.releaseDate ? formatDate(work.releaseDate) : "情報未登録";
}